'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled, asText } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextLink, PrismicNextImage } from '@prismicio/next';
import HighlightedText from '@/components/HighlightedText/HighlightedText';
import { useIsClient } from '@/hooks';

/**
 * Props for `CalloutGridHero`.
 */
export type CalloutGridHeroProps =
  SliceComponentProps<Content.CalloutGridHeroSlice>;

/**
 * Component for "CalloutGridHero" Slices.
 * Standard layout: callouts grid above main heading, description, and CTA.
 */
const CalloutGridHero: FC<CalloutGridHeroProps> = ({ slice }) => {
  const {
    eyebrow,
    subtitle,
    callout_items,
    main_heading,
    secondary_heading,
    description,
    primary_cta,
    social_media_icons,
  } = slice.primary;
  const isPlusAbout = slice.variation === 'plusAbout';
  const secondHeadline = isPlusAbout ? slice.primary.second_headline : null;
  const secondDescription = isPlusAbout
    ? slice.primary.second_description
    : null;
  const secondSocialLinks = isPlusAbout
    ? slice.primary.second_social_links
    : [];

  const headerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const secondSectionRef = useRef<HTMLDivElement>(null);
  const isClient = useIsClient();

  useEffect(() => {
    if (!isClient) {
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const headerEl = headerRef.current;
    const gridEl = gridRef.current;
    const ctaEl = ctaRef.current;
    const secondSectionEl = secondSectionRef.current;

    if (headerEl) {
      headerEl.style.transitionDelay = '0ms';
      observer.observe(headerEl);
    }

    if (gridEl) {
      gridEl.style.transitionDelay = '200ms';
      observer.observe(gridEl);
    }

    if (ctaEl) {
      ctaEl.style.transitionDelay = '400ms';
      observer.observe(ctaEl);
    }

    if (secondSectionEl) {
      secondSectionEl.style.transitionDelay = isPlusAbout ? '500ms' : '0ms';
      observer.observe(secondSectionEl);
    }

    return () => {
      if (headerEl) {
        observer.unobserve(headerEl);
      }
      if (gridEl) {
        observer.unobserve(gridEl);
      }
      if (ctaEl) {
        observer.unobserve(ctaEl);
      }
      if (secondSectionEl) {
        observer.unobserve(secondSectionEl);
      }
    };
  }, [isClient, isPlusAbout]);

  const handleScrollToForm = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderSocialLinks = (
    links:
      | Content.CalloutGridHeroSliceDefaultPrimarySocialMediaIconsItem[]
      | Content.CalloutGridHeroSlicePlusAboutPrimarySocialMediaIconsItem[]
      | Content.CalloutGridHeroSlicePlusAboutPrimarySecondSocialLinksItem[],
    className = 'p-2 hover:opacity-80 transition-opacity',
    iconClassName = 'w-6 h-6',
  ) => {
    if (!links || links.length === 0) {
      return null;
    }

    return (
      <div className="flex gap-4 items-center justify-center">
        {links.map((item, index) => (
          <PrismicNextLink key={index} field={item.link} className={className}>
            {isFilled.image(item.icon) ? (
              <PrismicNextImage field={item.icon} className={iconClassName} />
            ) : null}
          </PrismicNextLink>
        ))}
      </div>
    );
  };

  // Get current year for copyright
  const currentYear = new Date().getFullYear();

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      style={{
        background: 'linear-gradient(180deg, #0E1110 0%, #0F423A 100%)',
      }}
    >
      {/* Main Content */}
      <div className="px-5 md:px-16 pt-[60px] pb-[10px]">
        <div className="max-w-[1040px] mx-auto flex flex-col gap-[112px] items-center">
          {/* Section 1: Eyebrow, Subtitle, and Callout Grid */}
          <div className="flex flex-col gap-10 items-start justify-center w-full">
            {/* Header with Eyebrow and Subtitle */}
            <div
              ref={headerRef}
              className="flex flex-col gap-6 items-center justify-center w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
            >
              {/* Eyebrow with highlight support */}
              {isFilled.richText(eyebrow) && (
                <h2
                  className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-center capitalize"
                  style={{ color: '#FFFFFF' }}
                >
                  <HighlightedText
                    text={asText(eyebrow) || ''}
                    highlightClassName="text-[#05f273]"
                  />
                </h2>
              )}

              {/* Subtitle */}
              {isFilled.richText(subtitle) && (
                <PrismicRichText
                  field={subtitle}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-lg font-light leading-[1.4] text-white text-center">
                        {children}
                      </p>
                    ),
                  }}
                />
              )}
            </div>

            {/* Callout Grid */}
            {callout_items && callout_items.length > 0 && (
              <div
                ref={gridRef}
                className="flex flex-col md:flex-row gap-6 items-stretch w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
              >
                {callout_items.map((item, index) => (
                  <div
                    key={index}
                    className="flex-1 flex flex-col gap-2 p-6 rounded-[24px] text-white"
                    style={{
                      backgroundImage: `url("data:image/svg+xml;utf8,<svg viewBox='0 0 217 170' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'><rect x='0' y='0' height='100%' width='100%' fill='url(%23grad)' opacity='0.15'/><defs><radialGradient id='grad' gradientUnits='userSpaceOnUse' cx='0' cy='0' r='10' gradientTransform='matrix(-20.81 -0.0000097359 -27.912 -302.87 208.1 0.000097359)'><stop stop-color='rgba(255,255,255,0.5)' offset='0'/><stop stop-color='rgba(192,239,231,0.625)' offset='0.11667'/><stop stop-color='rgba(129,223,207,0.75)' offset='0.23334'/><stop stop-color='rgba(98,215,194,0.8125)' offset='0.29167'/><stop stop-color='rgba(66,207,182,0.875)' offset='0.35001'/><stop stop-color='rgba(35,199,170,0.9375)' offset='0.40834'/><stop stop-color='rgba(19,195,164,0.96875)' offset='0.43751'/><stop stop-color='rgba(3,191,158,1)' offset='0.46668'/><stop stop-color='rgba(19,195,164,1)' offset='0.50001'/><stop stop-color='rgba(35,199,170,1)' offset='0.53334'/><stop stop-color='rgba(66,207,182,1)' offset='0.60001'/><stop stop-color='rgba(98,215,194,1)' offset='0.66667'/><stop stop-color='rgba(129,223,207,1)' offset='0.73334'/><stop stop-color='rgba(192,239,231,1)' offset='0.86667'/><stop stop-color='rgba(255,255,255,1)' offset='1'/></radialGradient></defs></svg>")`,
                      backgroundSize: '100% 100%',
                    }}
                  >
                    {/* Callout Title */}
                    {item.title && (
                      <h3
                        className="text-xl font-semibold leading-[1.2] capitalize"
                        style={{ color: '#FFFFFF' }}
                      >
                        {item.title}
                      </h3>
                    )}

                    {/* Callout Description */}
                    {item.description && (
                      <p
                        className="text-[16px] leading-[140%]"
                        style={{ color: '#FFFFFF', fontWeight: 300 }}
                      >
                        {item.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section 2: Main Heading, Secondary Heading, Description, and CTA */}
          <div
            ref={ctaRef}
            className="flex flex-col gap-10 items-center justify-center w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {/* Headings */}
            <div className="flex flex-col gap-4 items-start w-full">
              <div className="flex flex-col gap-2 items-center font-medium text-[40px] md:text-[80px] text-center w-full leading-none">
                {/* Main Heading - White text */}
                {isFilled.richText(main_heading) && (
                  <PrismicRichText
                    field={main_heading}
                    components={{
                      paragraph: ({ children }) => (
                        <span style={{ color: '#FFFFFF' }}>{children}</span>
                      ),
                      heading1: ({ children }) => (
                        <span style={{ color: '#FFFFFF' }}>{children}</span>
                      ),
                      heading2: ({ children }) => (
                        <span style={{ color: '#FFFFFF' }}>{children}</span>
                      ),
                      heading3: ({ children }) => (
                        <span style={{ color: '#FFFFFF' }}>{children}</span>
                      ),
                    }}
                  />
                )}

                {/* Secondary Heading - Gradient text */}
                {isFilled.richText(secondary_heading) && (
                  <p
                    className="w-full"
                    style={{
                      background:
                        'radial-gradient(ellipse at center, #729e97 0%, #5d877f 25%, #496f68 50%, #345751 75%, #203f3a 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {asText(secondary_heading)}
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="flex flex-col items-center justify-center w-full">
                {isFilled.richText(description) && (
                  <PrismicRichText
                    field={description}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="text-lg font-light leading-[1.4] text-center text-white w-full">
                          {children}
                        </p>
                      ),
                    }}
                  />
                )}
              </div>
            </div>

            {/* CTA Button */}
            {isFilled.link(primary_cta) ? (
              <PrismicNextLink field={primary_cta} className="primary-button">
                {primary_cta.text || 'Download Now'}
              </PrismicNextLink>
            ) : (
              <button
                onClick={handleScrollToForm}
                className="primary-button cursor-pointer"
              >
                {primary_cta.text || 'Download Now'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Social Links Section */}
      {social_media_icons && social_media_icons.length > 0 && (
        <div className="px-5 md:px-16 py-[30px]">
          <div className="max-w-[1040px] mx-auto flex justify-center">
            {renderSocialLinks(social_media_icons)}
          </div>
        </div>
      )}

      {isPlusAbout && (
        <div className="mt-16 px-5 md:px-16 pb-8 md:pb-10">
          <div
            ref={secondSectionRef}
            className="max-w-[760px] mx-auto flex flex-col items-center text-center gap-6 opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {isFilled.richText(secondHeadline) && (
              <div className="text-white">
                <PrismicRichText
                  field={secondHeadline}
                  components={{
                    heading1: ({ children }) => (
                      <h2 className="text-[28px] md:text-[38px] font-medium leading-[1.2]">
                        {children}
                      </h2>
                    ),
                    heading2: ({ children }) => (
                      <h2 className="text-[28px] md:text-[38px] font-medium leading-[1.2]">
                        {children}
                      </h2>
                    ),
                    paragraph: ({ children }) => (
                      <p className="text-[28px] md:text-[38px] font-medium leading-[1.2]">
                        {children}
                      </p>
                    ),
                  }}
                />
              </div>
            )}

            {isFilled.richText(secondDescription) && (
              <PrismicRichText
                field={secondDescription}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-lg font-normal leading-[1.5] text-white/80">
                      {children}
                    </p>
                  ),
                }}
              />
            )}

            {renderSocialLinks(secondSocialLinks)}
          </div>
        </div>
      )}

      {/* Copyright Section */}
      <div className="border-t border-b border-[#666] px-5 md:px-16 py-6">
        <p className="text-sm font-normal leading-[1.5] text-[#ccc] text-center">
          © {currentYear} Devsu All rights reserved.
        </p>
      </div>
    </section>
  );
};

export default CalloutGridHero;
